import type React from "react";
import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Box,
  CircularProgress,
  Paper,
  Container,
  Chip,
  Avatar,
  Stack,
  Divider,
  InputAdornment,
  Fade,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Today,
  Event,
  Person,
  Phone,
  LocationOn,
  Description,
  AttachFile,
  ArrowBack,
  Send,
  CheckCircle,
  WarningAmber,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { getEmployeeLeave } from "../../../api/LeaveService";
import FileUploader from "../../../components/disciplinary/FileUploader";
import {
  Employee,
  getAllEmployees,
} from "../../saas_sales_app/outstanding/OutstandingAnalysisService";
import { IonContent, IonPage } from "@ionic/react";
import {
  baseUrl,
  config,
  requestID,
  calculateDaysBetween,
} from "../../../common/utils";

// Styled components for modern look
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
  overflow: "visible",
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(3),
  borderRadius: "16px 16px 0 0",
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 8,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& fieldset": {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      },
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& fieldset": {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      },
    },
  },
}));

// Helper function to format date safely
const formatDate = (date: any): string => {
  if (!date) return '';
  
  try {
    // If it's a Dayjs object (commonly used with MUI DatePicker)
    if (date?.$d) {
      return new Date(date.$d).toLocaleDateString();
    }
    // If it's a native Date object
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    // If it's a string that can be converted to Date
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    // If it's an object with toDate method (like Firebase Timestamp)
    if (typeof date?.toDate === 'function') {
      return date.toDate().toLocaleDateString();
    }
    
    return String(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
};

// Helper function to convert to ISO string safely
const toISODateString = (date: any): string => {
  if (!date) return '';
  
  try {
    let dateObj: Date;
    
    if (date?.$d) {
      dateObj = new Date(date.$d);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (typeof date?.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      dateObj = new Date(date);
    }
    
    return dateObj.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error converting date to ISO string:', error);
    return '';
  }
};

const ApplyLeavePage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [remainingDays, setRemainingDays] = useState<number>(
    (location.state as { remainingDays?: number })?.remainingDays ?? 10
  );

  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [leaveType, setLeaveType] = useState("");
  const [department, setDepartment] = useState("");
  const [reliefPersonId, setReliefPersonId] = useState("");
  const [reliefPersonName, setReliefPersonName] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [location_, setLocation] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingLeaveData, setLoadingLeaveData] = useState(false);
  const [requestedDays, setRequestedDays] = useState<number>(0);

  // Fetch employees for relief person dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const employeeData = await getAllEmployees();
        setEmployees(employeeData);
      } catch (err) {
        console.error("Failed to fetch employees:", err);
        setError("Failed to load employee data. Please try again later.");
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, []);

  // Fetch leave data if remaining days weren't passed in location state
  useEffect(() => {
    if (location?.state?.remainingDays === undefined) {
      const fetchLeaveData = async () => {
        setLoadingLeaveData(true);
        try {
          const leaveData = await getEmployeeLeave();
          let usedDays = 0;
          leaveData.forEach((app) => {
            const daysRequested = Number(app.days_requested) || app.days_requested || 0;
            if (app.status.toLowerCase() === "approved") {
              usedDays += daysRequested;
            }
          });
          const remaining = 30 - usedDays;
          setRemainingDays(remaining);
        } catch (err) {
          console.error("Failed to fetch leave data:", err);
        } finally {
          setLoadingLeaveData(false);
        }
      };

      fetchLeaveData();
    }
  }, [location.state]);

  // Calculate requested days whenever dates change
  useEffect(() => {
    if (startDate && endDate) {
      // Convert to Date objects for calculation
      const start = startDate?.$d ? new Date(startDate.$d) : new Date(startDate);
      const end = endDate?.$d ? new Date(endDate.$d) : new Date(endDate);
      
      const days = calculateDaysBetween(start, end);
      setRequestedDays(days);
    } else {
      setRequestedDays(0);
    }
  }, [startDate, endDate]);

  const validateDates = (): boolean => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return false;
    }

    // Convert to Date objects for comparison
    const start = startDate?.$d ? new Date(startDate.$d) : new Date(startDate);
    const end = endDate?.$d ? new Date(endDate.$d) : new Date(endDate);

    if (start > end) {
      setError("End date cannot be before start date");
      return false;
    }

    const days = calculateDaysBetween(start, end);
    if (days > remainingDays) {
      setError(
        `You've requested ${days} days, but you only have ${remainingDays} days remaining. Please adjust your dates.`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    if (!validateDates()) {
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("leave_type", leaveType);
    formData.append("department", department);
    formData.append("start_date", toISODateString(startDate));
    formData.append("end_date", toISODateString(endDate));
    formData.append("reliefPersonId", reliefPersonId);
    formData.append("reliefPersonName", reliefPersonName);
    formData.append("contactInfo", contactInfo);
    formData.append("location", location_);
    formData.append("reason", reason);
    formData.append("requestID", requestID());

    files.forEach((file) => {
      formData.append("documents", file);
    });

    try {
      const res = await axios.post(baseUrl() + "leaves/applyleave", formData, config);
      history.push("/folder/view-leaves/View%20Leaves");
    } catch (err) {
      console.error("Error submitting leave application:", err);
      setError(
        err instanceof Error ? err.message : "Failed to submit leave application"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage className="pushDown">
      <IonContent>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => history.push("/folder/view-leaves/View%20Leaves")}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              Back to Leaves
            </Button>
            
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <Event sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  Apply for Leave
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Submit your leave request with all necessary details
                </Typography>
              </Box>
            </Stack>

            {/* Status Overview */}
            <Fade in={true} timeout={800}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'background.default',
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'divider',
                  mb: 3
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <CheckCircle color="success" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Available Days
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {remainingDays} days
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <WarningAmber color={requestedDays > remainingDays ? "error" : "warning"} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Requested Days
                        </Typography>
                        <Typography 
                          variant="h5" 
                          fontWeight="bold"
                          color={requestedDays > remainingDays ? "error" : "text.primary"}
                        >
                          {requestedDays} days
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <StatusChip
                      label={requestedDays > remainingDays ? "Exceeds Limit" : "Within Limit"}
                      color={requestedDays > remainingDays ? "error" : "success"}
                      variant="filled"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Fade>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <StyledCard>
            <StyledHeader>
              <Typography variant="h5" fontWeight="bold">
                Leave Application Form
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Fill in the details below to submit your leave request
              </Typography>
            </StyledHeader>

            <CardContent sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Leave Type & Department */}
                <Grid item xs={12} md={6}>
                  <StyledFormControl fullWidth required>
                    <InputLabel id="leave-type-label">Leave Type</InputLabel>
                    <Select
                      labelId="leave-type-label"
                      value={leaveType}
                      label="Leave Type"
                      onChange={(e) => setLeaveType(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <Event />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="annual">Annual Leave</MenuItem>
                      <MenuItem value="sick">Sick Leave</MenuItem>
                      <MenuItem value="emergency">Emergency Leave</MenuItem>
                      <MenuItem value="travel">Travel Leave</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </StyledFormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledFormControl fullWidth required>
                    <InputLabel id="department-label">Department</InputLabel>
                    <Select
                      labelId="department-label"
                      value={department}
                      label="Department"
                      onChange={(e) => setDepartment(e.target.value)}
                    >
                      <MenuItem value="engineering">Engineering</MenuItem>
                      <MenuItem value="marketing">Marketing</MenuItem>
                      <MenuItem value="finance">Finance</MenuItem>
                      <MenuItem value="hr">Human Resources</MenuItem>
                      <MenuItem value="operations">Operations</MenuItem>
                    </Select>
                  </StyledFormControl>
                </Grid>

                <Divider sx={{ width: '100%', my: 2 }} />

                {/* Date Selection */}
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    disablePast
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Today />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                  {startDate && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Selected: {formatDate(startDate)}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    minDate={startDate || undefined}
                    disablePast
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: requestedDays > remainingDays,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Today />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                  {endDate && (
                    <Typography 
                      variant="caption" 
                      color={requestedDays > remainingDays ? "error" : "text.secondary"}
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {requestedDays > remainingDays 
                        ? `Exceeds available days by ${requestedDays - remainingDays} days`
                        : `${requestedDays} day${requestedDays !== 1 ? 's' : ''} requested`
                      }
                    </Typography>
                  )}
                </Grid>

                <Divider sx={{ width: '100%', my: 2 }} />

                {/* Relief Person */}
                <Grid item xs={12}>
                  <StyledFormControl fullWidth required>
                    <InputLabel id="relief-person-label">Relief Person</InputLabel>
                    <Select
                      labelId="relief-person-label"
                      value={reliefPersonId}
                      label="Relief Person"
                      onChange={(e) => {
                        const selectedId = e.target.value as string;
                        setReliefPersonId(selectedId);
                        const selectedEmployee = employees.find(
                          (emp) => emp.user_id === selectedId
                        );
                        if (selectedEmployee) {
                          setReliefPersonName(selectedEmployee.fullName);
                        }
                      }}
                      disabled={loadingEmployees}
                      startAdornment={
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      }
                    >
                      {loadingEmployees ? (
                        <MenuItem value="" disabled>
                          <CircularProgress size={20} sx={{ mr: 2 }} />
                          Loading employees...
                        </MenuItem>
                      ) : (
                        employees.map((employee) => (
                          <MenuItem key={employee.id} value={employee.user_id}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {employee.fullName.split(' ').map(n => n[0]).join('')}
                              </Avatar>
                              <Box>
                                <Typography variant="body1">{employee.fullName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {employee.department}
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </StyledFormControl>
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <StyledTextField
                    label="Contact During Leave"
                    placeholder="Phone number or email where you can be reached"
                    fullWidth
                    required
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    label="Location During Leave"
                    placeholder="Where will you be during your leave?"
                    fullWidth
                    required
                    value={location_}
                    onChange={(e) => setLocation(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Reason */}
                <Grid item xs={12}>
                  <StyledTextField
                    label="Reason for Leave"
                    placeholder="Please provide details about your leave request..."
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Description />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* File Upload */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                      <AttachFile />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Supporting Documents
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Upload supporting documents (doctor's note, invitation, etc.)
                        </Typography>
                      </Box>
                    </Stack>
                    <FileUploader
                      onFilesChange={setFiles}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      maxSize={5}
                    />
                  </Paper>
                </Grid>

                {/* Submit Buttons */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => history.push("/folder/view-leaves/View%20Leaves")}
                      disabled={isSubmitting}
                      size="large"
                      sx={{ borderRadius: 2, px: 4 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={
                        isSubmitting ||
                        requestedDays > remainingDays ||
                        requestedDays === 0
                      }
                      onClick={handleSubmit}
                      size="large"
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                      sx={{ borderRadius: 2, px: 4 }}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default ApplyLeavePage;